import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { AppState } from '../../../reducers';
import {
  selectAuthLoggedIn, selectAuthUser, selectAuthUserRole,
  selectUserNotifications, selectAuthUserProfileImageUrl, selectAuthUserOrgCountry
} from '../../../features/auth/auth.selectors';
import { LogoutComponent } from '../../logout/logout.component';
import { map, filter, startWith, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ROLES } from '../../../global/roles';
import { LoadUserNotificationsAction, MarkUserNotificationAction } from '../../../features/auth/auth.actions';
import { SetDocumentsTypeFilterAction } from '../../../features/documents/documents.actions';
import { User } from '../../../features/administration/user/user.models';
import { Subscription, BehaviorSubject, combineLatest, of } from 'rxjs';
import { CONFIG } from '../../../../config';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { COUNTRIES } from '../../../global/countries';
import { SetMainFilterAction, SetSelectedCountryAction } from '../main.actions';
import { selectCountrySelected } from '../main.selectors';
import { isEqual } from 'lodash';
import { SetDataOverviewAccountAction, SetDataOverviewClientAction } from '../../../features/administration/administration.actions';
import { DropdownTriggerType } from 'atlas-menu';
import { FieldSelectOption, FieldSize } from 'atlas-field';
import { IconSizes } from 'atlas-icon';
@Component({
  standalone: false,
  selector: 'dsp-header',
  templateUrl: 'header.component.html',
  styleUrls: ['header.component.scss']
})
export class HeaderComponent {
  @ViewChild('logoutComponent')
  public modal: LogoutComponent;

  public countries = Object.values(COUNTRIES);
  countryDropDownSize = FieldSize.small;
  selectedCountry = '';

  options: FieldSelectOption[] = [{
    label: 'Australia',
    value: 'au',
    leadingContent: {
      type: 'icon',
      icon: 'australia',
      collection: 'flags',
    },
  },
  {
    label: 'New Zealand',
    value: 'nz',
    leadingContent: {
      type: 'icon',
      icon: 'new-zealand',
      collection: 'flags',
    },

  }];

  dropdownTriggerTypeCustom = DropdownTriggerType.Custom;
  dropdownTriggerTypeIconButton = DropdownTriggerType.IconButton;
  iconSize = IconSizes.large;


  public ICON_MAP = {
    Transaction: 'compare_arrows',
    Statement: 'insert_drive_file',
    ClientDocuments: 'chrome_reader_mode',
    StrategicResearch: 'chrome_reader_mode',
    ProductDocuments: 'chrome_reader_mode'
  };
  public ROW_MAP = {
    Transaction: (row) => `Transactions for ${row.accounts} is now available`,
    Statement: (row) => `Statements for ${row.accounts} is now available`,
    ClientDocuments: (row) => `Client Documents ${row.name} is now available`,
    StrategicResearch: (row) => `Strategic Research ${row.name} is now available`,
    ProductDocuments: (row) => `Product Documents ${row.name} is now available`
  };
  public HEADER_ROW_MAP = {
    Transaction: (row) => `Transactions`,
    Statement: (row) => `Statements`,
    ClientDocuments: (row) => `Client Documents`,
    StrategicResearch: (row) => `Strategic Research`,
    ProductDocuments: (row) => `Product Documents`
  };
  public ROW_ROUTING = {
    Transaction: 'transactions/summary',
    Statement: 'statements',
    ClientDocuments: 'documents',
    StrategicResearch: 'documents',
    ProductDocuments: 'documents',
    settings: 'profile/notifications',
  };

  public user$: Observable<User | undefined>;
  public logged$: Observable<boolean>;
  public admin$: Observable<boolean>;
  public superadmin$: Observable<boolean>;
  public notifications$: Observable<Notification[]>;
  public profileImageUrl$: BehaviorSubject<{} | null> = new BehaviorSubject<{} | null>(null);
  public profileImageDataUrl$: Observable<any>;
  public imageUrl: any;
  public loading = true;
  public loadingCard = true;
  public imageLoadFailed = false;
  private readonly subscription: Subscription = new Subscription();


  public constructor(
    public readonly router: Router,
    private readonly store: Store<AppState>,
    // ,
    private readonly httpClient: HttpClient,
    private readonly domSanitizer: DomSanitizer,
    private readonly elementRef: ElementRef,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.user$ = this.store.pipe(select(selectAuthUser));
    this.logged$ = this.store.pipe(select(selectAuthLoggedIn));
    this.notifications$ = this.store.pipe(select(selectUserNotifications));
    this.admin$ = this.store.pipe(
      select(selectAuthUserRole),
      filter(role => !!role),
      map(role => role !== ROLES.client && !!environment.internal),
      startWith(false)
    );
    this.superadmin$ = this.store.pipe(
      select(selectAuthUserRole),
      filter(role => !!role),
      map(role => role === ROLES.superadministrator),
      startWith(false)
    );
    this.user$.subscribe(result => {
      if (result) {
        this.store.dispatch(new LoadUserNotificationsAction());
      }
    });

    this.profileImageDataUrl$ = this.store.pipe(
      select(selectAuthUserProfileImageUrl),
      switchMap(profileImageUrl => {
        return profileImageUrl ? this.loadImage(profileImageUrl) : of(null);
      })
    );
    this.profileImageDataUrl$.subscribe(dataUrl => {
      this.imageUrl = dataUrl;
      this.imageLoadFailed = !dataUrl;
      this.cdr.markForCheck();
    });
    this.subscription.add(this.subscribeCountry());
  }
  onLoad() {
    this.loading = false;
  }
  onLoadError() {
    this.loading = true;
  }
  onLoadCard() {
    this.loadingCard = false;
  }
  onLoadCardError() {
    this.loadingCard = true;
  }

  public logout(): void {
    this.modal.open();
  }

  public markAsRead(row): void {
    if (row._id && !row.read) {
      row.read = true;
      this.store.dispatch(new MarkUserNotificationAction(row._id));
    }
    this.router.navigate([this.ROW_ROUTING[row.type]]);
    if (row.type === 'StrategicResearch') {
      this.store.dispatch(new SetDocumentsTypeFilterAction('strategic-research'));
    } else if (row.type === 'ClientDocuments') {
      this.store.dispatch(new SetDocumentsTypeFilterAction('client-documents'));
    } else if (row.type === 'ProductDocuments') {
      this.store.dispatch(new SetDocumentsTypeFilterAction('product-documents'));
    }
  }
  private loadImage(url: any): Observable<any> {
    if (typeof url === 'string' && url.trim() !== '') {
      return this.httpClient
        .get(url, { responseType: 'blob', withCredentials: true })
        .pipe(
          map(blob => {
            if (blob && blob.size > 0) {
              const blobUrl = URL.createObjectURL(blob);
              return blobUrl;
            } else {
              return null;
            }
          }),
          catchError(err => {
            return of(null);
          })
        );
    } else {
      return of(null);
    }
  }

  onChangeCountry(event) {
    this.store.dispatch(new SetSelectedCountryAction(event));
  }

  private subscribeCountry(): Subscription {
    return combineLatest([
      this.store.pipe(select(selectAuthUserOrgCountry)),
      this.store.pipe(select(selectCountrySelected))
    ]).pipe(
      filter(([authUserOrgCountry, selectedCountry]) =>
        !!authUserOrgCountry
      ),
      map(([authUserOrgCountry, selectedCountry]) =>
        selectedCountry !== undefined ? selectedCountry : authUserOrgCountry
      ),
    ).subscribe(country => {
      this.store.dispatch(new SetSelectedCountryAction(country));
      this.store.dispatch(new SetMainFilterAction({
        client: [],
        account: []
      }));
      this.store.dispatch(new SetDataOverviewClientAction([]));
      this.store.dispatch(new SetDataOverviewAccountAction([]));
      this.selectedCountry = country;
    });
  }

  onLogoClick() {
    this.router.navigate(['/overview']);
  }

  onAppNameClick() {
    this.router.navigate(['/overview']);
  }
}
